import React, { useEffect, useRef, useState } from 'react'
import { PuzzlePieceIcon, ExclamationCircleIcon, ArrowPathIcon, XMarkIcon } from '@heroicons/react/24/outline'

import useGlobalTheme from '../../hooks/useGlobalTheme'
import { STORE_URL } from '../../utils/extensionBridge'

const MIN_DETECT_ANIMATION_MS = 2000

function hasChrome() {
	return typeof window !== 'undefined' && typeof window.chrome !== 'undefined'
}

function ExtensionRequiredModal({ status, onRecheck, onClose }) {
	const theme = useGlobalTheme()
	const isLight = theme === 'light'

	const [isDetecting, setIsDetecting] = useState(false)
	const mountedRef = useRef(true)
	useEffect(() => {
		mountedRef.current = true
		return () => {
			mountedRef.current = false
		}
	}, [])

	useEffect(() => {
		const onKey = e => {
			if (e.key === 'Escape') onClose?.()
		}
		window.addEventListener('keydown', onKey)
		return () => window.removeEventListener('keydown', onKey)
	}, [onClose])

	const handleRecheck = async () => {
		if (isDetecting) return
		setIsDetecting(true)
		await Promise.all([
			Promise.resolve(onRecheck?.()),
			new Promise(resolve => setTimeout(resolve, MIN_DETECT_ANIMATION_MS)),
		])
		if (mountedRef.current) setIsDetecting(false)
	}

	const isUpdate = status?.action === 'update'
	const browserSupported = hasChrome()

	const banner = !browserSupported
		? 'This browser is not supported'
		: isUpdate
			? 'Extension is out of date'
			: 'Extension not detected on this browser'

	const heading = !browserSupported
		? 'Open MultiBoard in Google Chrome'
		: isUpdate
			? 'Update the LoadLine extension'
			: 'Install the LoadLine extension'

	const body = !browserSupported
		? 'MultiBoard only works in Google Chrome.'
		: isUpdate
			? 'Update LoadLine from the Chrome Web Store to keep using MultiBoard. We’ll pick up automatically once it’s updated.'
			: 'The LoadLine extension must be installed for MultiBoard to work.'

	const installLabel = isUpdate ? 'Update' : 'Add to Chrome'

	return (
		<div
			className="fixed inset-0 z-[70] flex items-start justify-center bg-black/70 pt-32 px-4"
			role="dialog"
			aria-modal="true"
			aria-label="LoadLine extension required"
			onClick={onClose}
		>
			<div
				className={`w-full max-w-xl rounded-2xl overflow-hidden shadow-xl ${
					isLight ? 'bg-white' : 'bg-[#15202b] ring-1 ring-white/10'
				}`}
				onClick={e => e.stopPropagation()}
			>
				<div
					className={`flex items-center gap-2 px-5 py-3 border-b ${
						isLight
							? 'bg-red-50 border-red-100 text-red-700'
							: 'bg-red-500/10 border-red-500/20 text-red-300'
					}`}
				>
					<ExclamationCircleIcon className="h-5 w-5 shrink-0" />
					<span className="text-sm font-medium">{banner}</span>
					<button
						type="button"
						onClick={onClose}
						aria-label="Close"
						className={`ml-auto -mr-1.5 p-1 rounded-md transition-colors ${
							isLight ? 'hover:bg-red-100' : 'hover:bg-red-500/20'
						}`}
					>
						<XMarkIcon className="h-5 w-5" />
					</button>
				</div>

				<div className="px-6 pt-6 pb-6 flex items-start gap-4">
					<div
						className={`flex items-center justify-center w-12 h-12 rounded-xl shrink-0 ${
							isLight ? 'bg-sky-50' : 'bg-sky-500/15'
						}`}
					>
						<PuzzlePieceIcon className={`h-6 w-6 ${isLight ? 'text-sky-600' : 'text-sky-400'}`} />
					</div>
					<div className="min-w-0">
						<h2 className={`text-lg font-semibold ${isLight ? 'text-gray-900' : 'text-gray-100'}`}>
							{heading}
						</h2>
						<p
							className={`text-sm mt-2 leading-relaxed ${
								isLight ? 'text-gray-600' : 'text-gray-400'
							}`}
						>
							{body}
						</p>
					</div>
				</div>

				<div
					className={`flex items-center justify-between gap-3 px-6 py-4 border-t ${
						isLight ? 'bg-gray-50 border-gray-100' : 'bg-white/[0.03] border-white/[0.06]'
					}`}
				>
					<button
						type="button"
						onClick={handleRecheck}
						disabled={isDetecting}
						className={`inline-flex items-center gap-1.5 text-sm font-medium transition-colors disabled:cursor-default ${
							isLight
								? 'text-gray-600 hover:text-gray-900 disabled:text-gray-500'
								: 'text-gray-400 hover:text-gray-200 disabled:text-gray-400'
						}`}
					>
						{isDetecting && <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden />}
						{isDetecting ? 'Detecting…' : 'Try detecting again'}
					</button>

					{browserSupported && (
						<a
							href={STORE_URL}
							target="_blank"
							rel="noopener noreferrer"
							className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold bg-sky-500 text-white hover:bg-sky-600 transition-colors"
						>
							<PuzzlePieceIcon className="h-4 w-4" />
							{installLabel}
						</a>
					)}
				</div>
			</div>
		</div>
	)
}

export default ExtensionRequiredModal
